import {
  useEffect,
  useState,
} from "react";
import { Link } from "react-router-dom";
import {
  ACCEPTED_COOKIE_PREFERENCES,
  DEFAULT_COOKIE_PREFERENCES,
  getCookieConsent,
  saveCookieConsent,
  type CookiePreferences,
} from "../../constants/cookies";

type PreferenceToggleProps = {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
};

function PreferenceToggle({
  id,
  title,
  description,
  checked,
  disabled = false,
  onChange,
}: PreferenceToggleProps) {
  return (
    <div
      className={[
        "flex items-start justify-between",
        "gap-5 border-t",
        "border-[#E4EFED]",
        "py-4 first:border-t-0",
        "first:pt-0",
      ].join(" ")}
    >
      <div className="min-w-0">
        <label
          htmlFor={id}
          className={[
            "block text-sm font-semibold",
            "text-[#17313A]",
          ].join(" ")}
        >
          {title}
        </label>

        <p
          id={`${id}-description`}
          className={[
            "mt-1 max-w-[520px]",
            "text-xs leading-[1.6]",
            "text-[#64748B]",
          ].join(" ")}
        >
          {description}
        </p>
      </div>

      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={`${id}-description`}
        disabled={disabled}
        onClick={() => {
          if (!disabled) {
            onChange?.(!checked);
          }
        }}
        className={[
          "relative mt-1 h-6 w-11",
          "shrink-0 rounded-full",
          "transition-colors duration-200",
          "focus-visible:outline-none",
          "focus-visible:ring-2",
          "focus-visible:ring-[#159A92]",
          "focus-visible:ring-offset-2",
          checked
            ? "bg-[#159A92]"
            : "bg-[#CBD5E1]",
          disabled
            ? "cursor-not-allowed opacity-70"
            : "cursor-pointer",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute top-0.5",
            "h-5 w-5 rounded-full",
            "bg-white shadow-sm",
            "transition-transform duration-200",
            checked
              ? "translate-x-[22px]"
              : "translate-x-0.5",
          ].join(" ")}
        />
      </button>
    </div>
  );
}

export function CookieConsent() {
  const [isVisible, setIsVisible] =
    useState(false);

  const [showPreferences, setShowPreferences] =
    useState(false);

  const [preferences, setPreferences] =
    useState<CookiePreferences>(
      DEFAULT_COOKIE_PREFERENCES,
    );

  useEffect(() => {
    const storedConsent = getCookieConsent();

    if (storedConsent) {
      setPreferences(
        storedConsent.preferences,
      );

      return;
    }

    setIsVisible(true);
  }, []);

  /*
   * This event allows a future Cookie Settings
   * footer button to reopen the consent panel.
   */
  useEffect(() => {
    function openCookieSettings() {
      const storedConsent =
        getCookieConsent();

      setPreferences(
        storedConsent?.preferences ??
          DEFAULT_COOKIE_PREFERENCES,
      );

      setShowPreferences(true);
      setIsVisible(true);
    }

    window.addEventListener(
      "gluviacare:open-cookie-settings",
      openCookieSettings,
    );

    return () => {
      window.removeEventListener(
        "gluviacare:open-cookie-settings",
        openCookieSettings,
      );
    };
  }, []);

  function acceptAll() {
    saveCookieConsent(
      "accepted",
      ACCEPTED_COOKIE_PREFERENCES,
    );

    setPreferences(
      ACCEPTED_COOKIE_PREFERENCES,
    );

    setIsVisible(false);
    setShowPreferences(false);
  }

  function rejectOptional() {
    saveCookieConsent(
      "rejected",
      DEFAULT_COOKIE_PREFERENCES,
    );

    setPreferences(
      DEFAULT_COOKIE_PREFERENCES,
    );

    setIsVisible(false);
    setShowPreferences(false);
  }

  function savePreferences() {
    saveCookieConsent(
      "custom",
      preferences,
    );

    setIsVisible(false);
    setShowPreferences(false);
  }

  function updatePreference(
    name: "analytics" | "marketing",
    checked: boolean,
  ) {
    setPreferences((current) => ({
      ...current,
      [name]: checked,
    }));
  }

  if (!isVisible) {
    return null;
  }

  return (
    <div
      className={[
        "fixed inset-x-0 bottom-0",
        "z-[100] p-4",
        "sm:p-5",
        "md:p-6",
      ].join(" ")}
    >
      <div
        aria-hidden="true"
        className={[
          "fixed inset-0 -z-10",
          "bg-[#17313A]/20",
          "backdrop-blur-[2px]",
        ].join(" ")}
      />

      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
        className={[
          "mx-auto w-full",
          "max-w-[1180px]",
          "overflow-hidden rounded-2xl",
          "border border-[#D7EAE7]",
          "bg-white",
          "shadow-[0_24px_70px_rgba(23,49,58,0.20)]",
        ].join(" ")}
      >
        <div
          className={[
            "flex flex-col gap-6",
            "p-5 sm:p-6",
            "md:flex-row",
            "md:items-center",
            "md:justify-between",
            "md:gap-10",
            "lg:px-8 lg:py-7",
          ].join(" ")}
        >
          <div className="max-w-[670px]">
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className={[
                  "grid h-10 w-10",
                  "shrink-0 place-items-center",
                  "rounded-full",
                  "bg-[#E7F8F6]",
                  "text-xl",
                ].join(" ")}
              >
                🍪
              </span>

              <h2
                id="cookie-consent-title"
                className={[
                  "text-lg font-semibold",
                  "text-[#17313A]",
                  "md:text-xl",
                ].join(" ")}
              >
                Your privacy matters
              </h2>
            </div>

            <p
              id="cookie-consent-description"
              className={[
                "mt-3 text-sm",
                "leading-[1.7]",
                "text-[#52677B]",
              ].join(" ")}
            >
              We use essential cookies to keep
              GluviaCare+ working. With your
              permission, we may also use optional
              cookies to understand website usage
              and improve your experience.{" "}
              <Link
                to="/cookie-policy"
                className={[
                  "font-semibold",
                  "text-[#159A92]",
                  "underline",
                  "decoration-[#BFEAE5]",
                  "underline-offset-4",
                  "transition-colors",
                  "hover:text-[#117E78]",
                ].join(" ")}
              >
                Read our Cookie Policy
              </Link>
              .
            </p>
          </div>

          <div
            className={[
              "flex shrink-0 flex-col gap-3",
              "sm:flex-row sm:flex-wrap",
              "md:max-w-[390px]",
              "md:justify-end",
            ].join(" ")}
          >
            <button
              type="button"
              onClick={rejectOptional}
              className={[
                "inline-flex h-12",
                "items-center justify-center",
                "rounded-xl",
                "border border-[#BFEAE5]",
                "bg-white px-5",
                "text-sm font-semibold",
                "text-[#17313A]",
                "transition-all duration-200",
                "hover:border-[#159A92]",
                "hover:bg-[#F8FAF9]",
                "focus-visible:outline-none",
                "focus-visible:ring-2",
                "focus-visible:ring-[#159A92]",
                "focus-visible:ring-offset-2",
              ].join(" ")}
            >
              Reject optional
            </button>

            <button
              type="button"
              onClick={() => {
                setShowPreferences(
                  (current) => !current,
                );
              }}
              aria-expanded={showPreferences}
              aria-controls="cookie-preferences"
              className={[
                "inline-flex h-12",
                "items-center justify-center",
                "rounded-xl",
                "border border-[#159A92]",
                "bg-white px-5",
                "text-sm font-semibold",
                "text-[#159A92]",
                "transition-all duration-200",
                "hover:bg-[#E7F8F6]",
                "focus-visible:outline-none",
                "focus-visible:ring-2",
                "focus-visible:ring-[#159A92]",
                "focus-visible:ring-offset-2",
              ].join(" ")}
            >
              Manage preferences
            </button>

            <button
              type="button"
              onClick={acceptAll}
              className={[
                "inline-flex h-12",
                "items-center justify-center",
                "rounded-xl",
                "border border-[#159A92]",
                "bg-[#159A92] px-6",
                "text-sm font-semibold text-white",
                "transition-all duration-200",
                "hover:-translate-y-0.5",
                "hover:bg-[#117E78]",
                "hover:shadow-[0_8px_20px_rgba(21,154,146,0.18)]",
                "focus-visible:outline-none",
                "focus-visible:ring-2",
                "focus-visible:ring-[#159A92]",
                "focus-visible:ring-offset-2",
                "active:translate-y-0",
                "active:scale-[0.98]",
              ].join(" ")}
            >
              Accept all
            </button>
          </div>
        </div>

        {showPreferences && (
          <div
            id="cookie-preferences"
            className={[
              "border-t border-[#E4EFED]",
              "bg-[#F8FAF9]",
              "px-5 py-5",
              "sm:px-6",
              "lg:px-8 lg:py-6",
            ].join(" ")}
          >
            <div className="mx-auto max-w-[1080px]">
              <div className="mb-5">
                <h3
                  className={[
                    "text-base font-semibold",
                    "text-[#17313A]",
                  ].join(" ")}
                >
                  Cookie preferences
                </h3>

                <p
                  className={[
                    "mt-1 text-xs",
                    "leading-[1.6]",
                    "text-[#64748B]",
                  ].join(" ")}
                >
                  Choose which optional cookies
                  GluviaCare+ may use. Essential
                  cookies cannot be disabled.
                </p>
              </div>

              <PreferenceToggle
                id="essential-cookies"
                title="Essential cookies"
                description="Required for security, navigation, form functionality and remembering your privacy selection."
                checked
                disabled
              />

              <PreferenceToggle
                id="analytics-cookies"
                title="Analytics cookies"
                description="Help us understand how visitors use the website so we can improve performance and usability."
                checked={preferences.analytics}
                onChange={(checked) =>
                  updatePreference(
                    "analytics",
                    checked,
                  )
                }
              />

              <PreferenceToggle
                id="marketing-cookies"
                title="Marketing cookies"
                description="May be used to measure campaigns and provide more relevant GluviaCare+ communications."
                checked={preferences.marketing}
                onChange={(checked) =>
                  updatePreference(
                    "marketing",
                    checked,
                  )
                }
              />

              <div
                className={[
                  "mt-5 flex flex-col gap-3",
                  "sm:flex-row sm:justify-end",
                ].join(" ")}
              >
                <button
                  type="button"
                  onClick={() =>
                    setShowPreferences(false)
                  }
                  className={[
                    "inline-flex h-11",
                    "items-center justify-center",
                    "rounded-xl px-5",
                    "text-sm font-semibold",
                    "text-[#52677B]",
                    "transition-colors",
                    "hover:bg-white",
                    "hover:text-[#17313A]",
                  ].join(" ")}
                >
                  Back
                </button>

                <button
                  type="button"
                  onClick={savePreferences}
                  className={[
                    "inline-flex h-11",
                    "items-center justify-center",
                    "rounded-xl",
                    "bg-[#159A92] px-6",
                    "text-sm font-semibold",
                    "text-white",
                    "transition-all duration-200",
                    "hover:bg-[#117E78]",
                    "active:scale-[0.98]",
                    "focus-visible:outline-none",
                    "focus-visible:ring-2",
                    "focus-visible:ring-[#159A92]",
                    "focus-visible:ring-offset-2",
                  ].join(" ")}
                >
                  Save preferences
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}